import { useEffect, useState, useCallback } from "react";
import {
  getScreenshotInterval,
  updateScreenshotInterval,
  getScreenshotStatus,
  // testAutoScreenshot,
  fetchWorkspaces,
  getOrganizationStorage
} from "../../api/api";
// import "./Settings.css";

const Settings = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [screenshotStatus, setScreenshotStatus] = useState(null);
  const [lastLoadTime, setLastLoadTime] = useState(null);

  const [storage, setStorage] = useState({
    used: 0,
    limit: 0
  });


  // Simple settings state
  const [settings, setSettings] = useState({
    screenshotInterval: 600,
    workHoursStart: "09:00",
    workHoursEnd: "17:00",
    trackingDays: ["mon", "tue", "wed", "thu", "fri"],
    privacyBlur: false,
    screenshotQuality: 60,
    enableTracking: true,
    dailyReports: true,
    weeklyReports: false,
    screenshotBlurEnabled: false,
    screenshotBlurIntensity: 8,
    idleThreshold: 300,

    screenshotRetentionDays: 30 // NEW
  });
  const [errors, setErrors] = useState({});

  // Memoize load function to prevent infinite re-renders
  const loadWorkspaceSettings = useCallback(async (workspaceId) => {
    // Prevent too frequent loading
    const now = Date.now();
    if (lastLoadTime && (now - lastLoadTime < 2000)) {
      console.log("⏸️ Skipping load - too frequent");
      return;
    }

    console.log("🔄 Loading workspace settings for ID:", workspaceId);
    setLastLoadTime(now);

    try {
      // Get screenshot interval directly
      const intervalData = await getScreenshotInterval(workspaceId);
      console.log("✅ Got screenshot settings:", intervalData);

      if (intervalData &&
        intervalData.screenshot_interval !== null &&
        intervalData.screenshot_interval !== undefined) {
        setSettings(prev => {
          // Only update if value actually changed
          if (prev.screenshotInterval === intervalData.screenshot_interval &&
            prev.screenshotBlurEnabled === (intervalData.screenshot_blur_enabled || false) &&
            prev.screenshotBlurIntensity === (intervalData.screenshot_blur_intensity || 8) &&
            prev.idleThreshold === (intervalData.idle_threshold || 300)) {
            console.log("📊 No change in settings, skipping update");
            return prev;
          }
          console.log("📊 Updating settings:", {
            interval: intervalData.screenshot_interval,
            blur: intervalData.screenshot_blur_enabled || false,
            intensity: intervalData.screenshot_blur_intensity || 8,
            idleThreshold: intervalData.idle_threshold || 300
          });
          return {
            ...prev,
            screenshotInterval: intervalData.screenshot_interval,
            screenshotBlurEnabled: intervalData.screenshot_blur_enabled || false,
            screenshotBlurIntensity: intervalData.screenshot_blur_intensity || 8,
            screenshotQuality: intervalData.screenshot_quality || 60,
            idleThreshold: intervalData.idle_threshold || 300
          };
        });
      }

      // Try to get status (optional)
      try {
        const status = await getScreenshotStatus(workspaceId);
        if (status && status.success !== false) {
          setScreenshotStatus(status.data || null);
        }
      } catch (statusError) {
        // Ignore - not critical
      }

    } catch (error) {
      console.error("❌ Error loading workspace settings:", error);
    }

    try {
      const res = await getOrganizationStorage(workspaceId);

      console.log(" Storage API:", res);

      setStorage({
  used: Number(res?.used_bytes || res?.used || 0),
  limit: Number(res?.limit_bytes || res?.limit || 0)
});

    } catch (err) {
      console.error("Storage load error:", err);
    }




  }, [lastLoadTime]);

  // Load workspaces on mount - ONLY ONCE
  useEffect(() => {
    let mounted = true;
    let loadTimeout = null;

    const loadWorkspaces = async () => {
      try {
        if (!user?.id) return;

        const workspacesData = await fetchWorkspaces(user.id);

        if (!mounted) return;

        setWorkspaces(workspacesData || []);

        if (workspacesData?.length > 0) {
          // Prefer previously selected workspace
          const workspaceToLoad =
            selectedWorkspace ||
            workspacesData.find(w => w.id === selectedWorkspace?.id) ||
            workspacesData[0];

          setSelectedWorkspace(workspaceToLoad);

          loadTimeout = setTimeout(() => {
            if (mounted && workspaceToLoad?.id) {
              loadWorkspaceSettings(workspaceToLoad.id);
            }
          }, 100);
        }
      } catch (error) {
        console.error("Failed to load workspaces:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadWorkspaces();

    return () => {
      mounted = false;
      if (loadTimeout) clearTimeout(loadTimeout);
    };
  }, [user, selectedWorkspace, loadWorkspaceSettings]);


  // Handle workspace selection
  // const handleWorkspaceChange = (workspace) => {
  //   console.log("🔄 Changing workspace to:", workspace.id);
  //   setSelectedWorkspace(workspace);
  //   // Load new workspace settings
  //   loadWorkspaceSettings(workspace.id);
  // };

  // Handle setting changes
  const handleSettingChange = (key, value) => {
    console.log(`✏️ Changing ${key} from ${settings[key]} to ${value}`);
    setSettings(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  // Handle interval change
  const handleIntervalChange = (value) => {
    console.log(`⏱️ Interval change input: ${value}`);

    const intValue = parseInt(value);
    if (!isNaN(intValue) && intValue >= 0) {
      handleSettingChange("screenshotInterval", intValue);
    }
  };

  // Validate settings
  const validateSettings = () => {
    const newErrors = {};

    if (settings.screenshotInterval !== undefined && settings.screenshotInterval !== 0) {
      if (settings.screenshotInterval < 30) {
        newErrors.screenshotInterval = "Minimum interval is 30 seconds (use 0 to disable)";
      }
    }

    // Add validation for idle threshold
    if (settings.idleThreshold < 30 || settings.idleThreshold > 3600) {
      newErrors.idleThreshold = "Idle threshold must be between 30 and 3600 seconds";
    }

    const start = parseInt(settings.workHoursStart.replace(":", ""));
    const end = parseInt(settings.workHoursEnd.replace(":", ""));
    if (start >= end) {
      newErrors.workHours = "End time must be after start time";
    }

    return newErrors;
  };

  // FIXED: Save settings to ALL workspaces (UI-safe, no snap-back)
  const saveSettings = async () => {
    if (workspaces.length === 0) {
      alert("No workspaces found");
      return;
    }

    const validationErrors = validateSettings();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    console.log("💾 Saving settings to ALL workspaces...");
    console.log("Settings to save:", {
      interval: settings.screenshotInterval,
      blurEnabled: settings.screenshotBlurEnabled,
      blurIntensity: settings.screenshotBlurIntensity,
      idleThreshold: settings.idleThreshold
    });

    setSaving(true);

    try {
      let successCount = 0;
      let errorCount = 0;
      const failedWorkspaces = [];

      // 🔁 Update ALL workspaces
      for (const workspace of workspaces) {
        try {
          const result = await updateScreenshotInterval(
            workspace.id,
            settings.screenshotInterval,
            settings.screenshotBlurEnabled,
            settings.screenshotBlurIntensity || 8,
            settings.screenshotQuality,
            settings.idleThreshold,
            settings.screenshotRetentionDays
          );

          if (result?.success) {
            successCount++;
          } else {
            errorCount++;
            failedWorkspaces.push(
              `Workspace ${workspace.name}: ${result?.message || "Unknown error"}`
            );
          }
        } catch (err) {
          errorCount++;
          failedWorkspaces.push(
            `Workspace ${workspace.name}: ${err.message}`
          );
        }
      }

      // HARD LOCK UI TO SAVED VALUE (prevents reverting to 600)
      setSettings(prev => ({
        ...prev,
        screenshotInterval: settings.screenshotInterval,
        screenshotBlurEnabled: settings.screenshotBlurEnabled,
        screenshotBlurIntensity: settings.screenshotBlurIntensity || 8,
        screenshotQuality: settings.screenshotQuality,
        idleThreshold: settings.idleThreshold
      }));

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      //  Summary message
      let message = `Settings saved successfully!\n\n`;
      message += `• Screenshot Interval: ${settings.screenshotInterval} seconds\n`;
      message += `• Idle Threshold: ${settings.idleThreshold} seconds\n`;
      message += `• Blur Screenshots: ${settings.screenshotBlurEnabled ? "Enabled" : "Disabled"}`;

      if (settings.screenshotBlurEnabled) {
        message += ` (Intensity: ${settings.screenshotBlurIntensity}/10)`;
      }

      message += `\n\nApplied to ${successCount} out of ${workspaces.length} workspaces`;

      if (errorCount > 0) {
        message += `\n\n ${errorCount} workspaces failed:\n`;
        message += failedWorkspaces.slice(0, 3).join("\n");
        if (failedWorkspaces.length > 3) {
          message += `\n...and ${failedWorkspaces.length - 3} more`;
        }
      }

      alert(message);

      //  IMPORTANT: DO NOT reload workspace settings here
      // Reloading is what caused the UI to snap back to 600

    } catch (error) {
      console.error(" Save error:", error);
      alert(`Failed to save settings: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };


  // Test screenshot functionality on first workspace
  // const handleTestScreenshots = async () => {
  //   if (workspaces.length === 0) return;

  //   const workspaceToTest = selectedWorkspace || workspaces[0];

  //   try {
  //     await testAutoScreenshot(workspaceToTest.id);
  //     alert(`Test screenshot request sent to ${workspaceToTest.name}!`);
  //   } catch (error) {
  //     console.error("Test failed:", error);
  //     alert("Test failed. Please check console.");
  //   }
  // };

  // Reset to defaults
  const resetToDefaults = () => {
    if (window.confirm(`Reset all settings to default for ALL ${workspaces.length} workspaces?`)) {
      setSettings(prev => ({
        ...prev,
        screenshotInterval: 600,
        screenshotBlurEnabled: false,
        screenshotBlurIntensity: 8,
        idleThreshold: 300
      }));
    }
  };

  // Format interval for display
  const formatInterval = (seconds) => {
    if (seconds === 0) return "Disabled";
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    return `${(seconds / 3600).toFixed(1)} hours`;
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <div className="settings-empty">
        <h2>No Workspaces Found</h2>
        <p>Create a workspace to configure settings.</p>
      </div>
    );
  }

  // STORAGE FORMATTER

  const formatStorage = (bytes) => {
  const value = Number(bytes);

  if (isNaN(value) || value <= 0) return "0 MB";

  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(value) / Math.log(1024));

  return `${(value / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};




  return (
    <div className="container-fluid">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Workspace Settings</h4>
          <p className="text-muted mb-0">
            Configure monitoring and automation settings
          </p>
        </div>

        {saveSuccess && (
          <div className="alert alert-success mb-0 py-2">
            Settings saved successfully!
          </div>
        )}
      </div>

      <div className="row">

        {/* Auto Screenshots */}
        <div className="col-lg-6">

          <div className="card bg-white border-0 rounded-3 mb-4">
            <div className="card-body p-4">

              <h5 className="mb-1">Auto Screenshots</h5>
              <p className="text-muted mb-4">
                Configure automatic screenshot capture
              </p>

              {/* Interval */}
              <div className="mb-4">

                <label className="form-label fw-semibold">
                  Screenshot Interval
                </label>

                <div className="d-flex flex-wrap gap-2 mb-3">

                  <button
                    className={`btn btn-sm ${settings.screenshotInterval === 0 ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => handleIntervalChange(0)}
                  >
                    Disabled
                  </button>

                  <button
                    className={`btn btn-sm ${settings.screenshotInterval === 30 ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => handleIntervalChange(30)}
                  >
                    30s
                  </button>

                  <button
                    className={`btn btn-sm ${settings.screenshotInterval === 60 ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => handleIntervalChange(60)}
                  >
                    1m
                  </button>

                  <button
                    className={`btn btn-sm ${settings.screenshotInterval === 300 ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => handleIntervalChange(300)}
                  >
                    5m
                  </button>

                  <button
                    className={`btn btn-sm ${settings.screenshotInterval === 600 ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => handleIntervalChange(600)}
                  >
                    10m
                  </button>

                </div>

                <input
                  type="number"
                  className="form-control"
                  value={settings.screenshotInterval}
                  onChange={(e) => handleIntervalChange(e.target.value)}
                />

                <small className="text-muted">
                  Current: {formatInterval(settings.screenshotInterval)}
                </small>

              </div>

              {/* Blur Screenshots */}
              <div className="form-check mb-4">

                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={settings.screenshotBlurEnabled}
                  onChange={(e) =>
                    handleSettingChange(
                      "screenshotBlurEnabled",
                      e.target.checked
                    )
                  }
                />

                <label className="form-check-label">
                  Blur Screenshots
                </label>

              </div>

              {/* Blur Intensity */}
              {settings.screenshotBlurEnabled && (
                <div className="mb-4">

                  <label className="form-label fw-semibold">
                    Blur Intensity ({settings.screenshotBlurIntensity}/10)
                  </label>

                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.screenshotBlurIntensity}
                    onChange={(e) =>
                      handleSettingChange(
                        "screenshotBlurIntensity",
                        parseInt(e.target.value)
                      )
                    }
                    className="form-range"
                  />

                </div>
              )}

              {/* Screenshot Quality */}
              <div>

                <label className="form-label fw-semibold">
                  Screenshot Quality ({settings.screenshotQuality}%)
                </label>

                <input
                  type="range"
                  min="30"
                  max="90"
                  step="5"
                  value={settings.screenshotQuality}
                  onChange={(e) =>
                    handleSettingChange(
                      "screenshotQuality",
                      parseInt(e.target.value)
                    )
                  }
                  className="form-range"
                />

              </div>

            </div>
          </div>
        </div>


        {/* Tracking Settings */}
        <div className="col-lg-6">

          <div className="card bg-white border-0 rounded-3 mb-4">
            <div className="card-body p-4">

              <h5 className="mb-1">Tracking Settings</h5>
              <p className="text-muted mb-4">
                Configure activity monitoring
              </p>

              {/* Idle Threshold */}
              <div className="mb-4">

                <label className="form-label fw-semibold">
                  Idle Time Threshold
                </label>

                <div className="input-group">

                  <input
                    type="number"
                    className="form-control"
                    value={settings.idleThreshold}
                    onChange={(e) =>
                      handleSettingChange(
                        "idleThreshold",
                        parseInt(e.target.value) || 300
                      )
                    }
                  />

                  <span className="input-group-text">
                    seconds
                  </span>

                </div>

                <small className="text-muted">
                  Time before employee is marked idle
                </small>

              </div>

              {/* Enable Tracking */}
              <div className="form-check">

                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={settings.enableTracking}
                  onChange={(e) =>
                    handleSettingChange(
                      "enableTracking",
                      e.target.checked
                    )
                  }
                />

                <label className="form-check-label">
                  Enable Activity Tracking
                </label>

              </div>

            </div>

          </div>

        </div>

        {/* Screenshot Retention */}
        <div className="col-lg-6">

          <div className="card bg-white border-0 rounded-3 mb-4">
            <div className="card-body p-4">

              <h5 className="mb-1">Screenshot Retention</h5>
              <p className="text-muted mb-4">
                Automatically delete old screenshots from database and cloud storage
              </p>

              <label className="form-label fw-semibold mb-3">
                Auto Delete After
              </label>

              <div className="d-flex flex-wrap gap-2 mb-3">

                <button
                  type="button"
                  className={`btn btn-sm ${settings.screenshotRetentionDays === 0 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => handleSettingChange("screenshotRetentionDays", 0)}
                >
                  Keep Forever
                </button>

                <button
                  type="button"
                  className={`btn btn-sm ${settings.screenshotRetentionDays === 7 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => handleSettingChange("screenshotRetentionDays", 7)}
                >
                  7 Days
                </button>

                <button
                  type="button"
                  className={`btn btn-sm ${settings.screenshotRetentionDays === 15 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => handleSettingChange("screenshotRetentionDays", 15)}
                >
                  15 Days
                </button>

                <button
                  type="button"
                  className={`btn btn-sm ${settings.screenshotRetentionDays === 30 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => handleSettingChange("screenshotRetentionDays", 30)}
                >
                  30 Days
                </button>

              </div>

              <div className="alert alert-warning mb-0">

                Old screenshots will be automatically deleted after the selected period.

                <br />

                This will remove them from:
                <ul className="mb-0 mt-2">
                  <li>Database records</li>
                  <li>Cloudinary storage</li>
                </ul>

              </div>

            </div>
          </div>

        </div>

        <div className="col-lg-6">
          <div className="card bg-white border-0 rounded-3 mb-4">
            <div className="card-body p-4">

              <h5 className="mb-1">Storage Usage</h5>
              <p className="text-muted mb-3">
                Storage used by screenshots in your organization
              </p>

              <h4 className="mb-2">
                {formatStorage(storage.used)}
              </h4>

              {storage.limit > 0 && (
                <small className="text-muted">
                  of {formatStorage(storage.limit)} used
                </small>
              )}

              {/* Progress bar */}
              {storage.limit > 0 && (
                <div className="progress mt-3" style={{ height: "8px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${Math.min(
                        (storage.used / storage.limit) * 100,
                        100
                      )}%`
                    }}
                  />
                </div>
              )}

            </div>
          </div>
        </div>

      </div>


      {/* Actions */}
      <div className="card bg-white border-0 rounded-3">
        <div className="card-body p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">

          <span className="text-muted">
            Changes apply to all {workspaces.length} workspaces
          </span>

          <div className="d-flex gap-2">

            <button
              className="btn btn-outline-secondary"
              onClick={resetToDefaults}
              disabled={saving}
            >
              Reset to Defaults
            </button>

            <button
              className="btn btn-primary"
              onClick={saveSettings}
              disabled={saving}
            >
              {saving
                ? `Saving...`
                : `Save Settings`}
            </button>

          </div>

        </div>


      </div>



    </div>
  );
};

export default Settings;